import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Building2,
  CheckCircle2,
  Crown,
  Info,
  KeyRound,
  Loader2,
  Shield,
  Trash2,
  Users,
  XCircle,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_OPTIONS = ['FREE', 'COMMUNITY', 'PRO', 'TEAM', 'FIRM'];
const TEAM_ROLE_OPTIONS = ['MEMBER', 'TEAM_ADMIN'];
const TEST_PLAN_OPTIONS = ['FREE', 'PRO', 'TEAM', 'COMMUNITY'];

function parseCsv(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseUsageLimits(value) {
  const raw = String(value || '').trim();
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return raw.split('\n').reduce((acc, line) => {
      const [key, ...rest] = line.split(':');
      if (!key || !rest.length) return acc;
      acc[key.trim()] = rest.join(':').trim();
      return acc;
    }, {});
  }
}

function StatCard({ icon: Icon, label, value, note }) {
  return (
    <div className="advoq-card p-5">
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl" style={{ background: 'var(--bg-elevated)', color: 'var(--accent-primary)' }}>
        <Icon size={18} />
      </div>
      <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{value}</p>
      <p className="mt-1 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{label}</p>
      {note ? <p className="mt-2 text-xs leading-5" style={{ color: 'var(--text-muted)' }}>{note}</p> : null}
    </div>
  );
}

function SectionCard({ title, body, children, action }) {
  return (
    <div className="advoq-card p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-display)' }}>{title}</h2>
          {body ? <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>{body}</p> : null}
        </div>
        {action || null}
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function TeamCard({ team, onSave, saving }) {
  const [email, setEmail] = useState('');
  const [teamRole, setTeamRole] = useState('MEMBER');

  return (
    <details className="rounded-2xl border p-4" style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-surface)' }}>
      <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
        <span>{team.name}</span>
        <span style={{ color: 'var(--text-muted)' }}>{(team.members || []).length} members</span>
      </summary>
      <div className="mt-4 space-y-3">
        {(team.members || []).map((member) => (
          <div key={`${team.team_id}-${member.email}`} className="flex flex-col gap-2 rounded-xl p-3 sm:flex-row sm:items-center sm:justify-between" style={{ background: 'var(--bg-elevated)' }}>
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{member.email}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{member.team_role}</p>
            </div>
            <select
              className="advoq-input text-sm"
              value={member.team_role}
              onChange={(event) => onSave(team.team_id, member.email, event.target.value)}
              disabled={saving}
            >
              {TEAM_ROLE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        ))}
        <div className="grid gap-3 md:grid-cols-[1fr_180px_140px]">
          <input
            className="advoq-input"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="member@firm.com"
          />
          <select className="advoq-input" value={teamRole} onChange={(event) => setTeamRole(event.target.value)}>
            {TEAM_ROLE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <button
            type="button"
            className="advoq-btn-primary"
            disabled={saving || !email.trim()}
            onClick={() => {
              onSave(team.team_id, email, teamRole);
              setEmail('');
              setTeamRole('MEMBER');
            }}
          >
            Save Member
          </button>
        </div>
      </div>
    </details>
  );
}

export default function AdminPage() {
  const { user, setUser, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [teams, setTeams] = useState([]);
  const [busyKey, setBusyKey] = useState('');
  const [overviewExpanded, setOverviewExpanded] = useState(false);
  const [programForm, setProgramForm] = useState({
    name: '',
    target_market: '',
    enabled_features: '',
    usage_limits: '',
    referral_emails: '',
  });
  const [teamForm, setTeamForm] = useState({
    name: '',
    admin_emails: '',
    member_emails: '',
  });

  const isOwner = Boolean(user?.is_advoq_owner);
  const hasAdminAccess = Boolean(user?.admin_console_access);

  const loadAll = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [metricsResp, usersResp, programsResp, teamsResp] = await Promise.all([
        fetch(`${API}/admin/metrics`, { headers, credentials: 'include' }),
        fetch(`${API}/admin/users?limit=200`, { headers, credentials: 'include' }),
        fetch(`${API}/admin/community-programs`, { headers, credentials: 'include' }),
        fetch(`${API}/admin/teams`, { headers, credentials: 'include' }),
      ]);

      if (!metricsResp.ok || !usersResp.ok || !programsResp.ok || !teamsResp.ok) {
        throw new Error('Failed to load admin data');
      }

      const [metricsData, usersData, programsData, teamsData] = await Promise.all([
        metricsResp.json(),
        usersResp.json(),
        programsResp.json(),
        teamsResp.json(),
      ]);
      setMetrics(metricsData);
      setUsers(usersData.users || []);
      setPrograms(programsData.programs || []);
      setTeams(teamsData.teams || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasAdminAccess) {
      setLoading(false);
      return;
    }
    loadAll();
    // `getAuthHeaders` identity is not stable enough to depend on here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAdminAccess]);

  const activePrograms = useMemo(
    () => programs.filter((program) => program.status === 'ACTIVE'),
    [programs],
  );

  const updateUser = async (targetUser, payload, successMessage) => {
    setBusyKey(`user-${targetUser.user_id}`);
    try {
      const response = await fetch(`${API}/admin/users/${targetUser.user_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update user');
      }
      setUsers((current) => current.map((item) => (item.user_id === targetUser.user_id ? data : item)));
      toast.success(successMessage);
      await loadAll();
    } catch (error) {
      toast.error(error.message || 'Failed to update user');
    } finally {
      setBusyKey('');
    }
  };

  const handleDeleteUser = async (targetUser) => {
    if (!window.confirm(`Delete ${targetUser.email}? This permanently removes the account and its saved data.`)) return;
    setBusyKey(`delete-${targetUser.user_id}`);
    try {
      const response = await fetch(`${API}/admin/users/${targetUser.user_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to delete user');
      toast.success('User deleted');
      await loadAll();
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    } finally {
      setBusyKey('');
    }
  };

  const switchContext = async ({ plan, clear = false }) => {
    setBusyKey(`switch-${plan || 'live'}`);
    try {
      const response = await fetch(`${API}/admin/switch-context`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ plan, clear }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to update staff testing mode');
      setUser(data);
      toast.success(clear ? 'Returned to live staff access' : `Testing as ${plan}`);
      await loadAll();
    } catch (error) {
      toast.error(error.message || 'Failed to update staff testing mode');
    } finally {
      setBusyKey('');
    }
  };

  const handleCreateProgram = async (event) => {
    event.preventDefault();
    setBusyKey('create-program');
    try {
      const response = await fetch(`${API}/admin/community-programs`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          name: programForm.name,
          target_market: programForm.target_market,
          enabled_features: parseCsv(programForm.enabled_features),
          usage_limits: parseUsageLimits(programForm.usage_limits),
          referral_emails: parseCsv(programForm.referral_emails),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to create community-tier program');
      toast.success(data.status === 'ACTIVE' ? 'Community-tier program launched' : 'Community-tier request submitted for owner approval');
      setProgramForm({ name: '', target_market: '', enabled_features: '', usage_limits: '', referral_emails: '' });
      await loadAll();
    } catch (error) {
      toast.error(error.message || 'Failed to create community-tier program');
    } finally {
      setBusyKey('');
    }
  };

  const reviewProgram = async (programId, status) => {
    setBusyKey(`program-${programId}`);
    try {
      const response = await fetch(`${API}/admin/community-programs/${programId}/review`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to review community-tier request');
      toast.success(status === 'ACTIVE' ? 'Community-tier request approved' : 'Community-tier request denied');
      await loadAll();
    } catch (error) {
      toast.error(error.message || 'Failed to review community-tier request');
    } finally {
      setBusyKey('');
    }
  };

  const handleCreateTeam = async (event) => {
    event.preventDefault();
    setBusyKey('create-team');
    try {
      const response = await fetch(`${API}/admin/teams`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          name: teamForm.name,
          admin_emails: parseCsv(teamForm.admin_emails),
          member_emails: parseCsv(teamForm.member_emails),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to create team');
      toast.success(`Team ${data.name} created`);
      setTeamForm({ name: '', admin_emails: '', member_emails: '' });
      await loadAll();
    } catch (error) {
      toast.error(error.message || 'Failed to create team');
    } finally {
      setBusyKey('');
    }
  };

  const updateTeamMember = async (teamId, email, teamRole) => {
    setBusyKey(`team-${teamId}`);
    try {
      const response = await fetch(`${API}/admin/teams/${teamId}/members`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ email, team_role: teamRole }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to update team member');
      toast.success('Team member updated');
      setTeams((current) => current.map((team) => (team.team_id === teamId ? data : team)));
      await loadAll();
    } catch (error) {
      toast.error(error.message || 'Failed to update team member');
    } finally {
      setBusyKey('');
    }
  };

  if (!hasAdminAccess) {
    return (
      <main className="app-main flex items-center justify-center">
        <div className="text-center">
          <Shield size={40} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
          <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>You don’t have access to the Advoq admin console.</p>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="app-main flex items-center justify-center">
        <Loader2 className="animate-spin" size={30} style={{ color: 'var(--accent-primary)' }} />
      </main>
    );
  }

  const overview = metrics?.overview || {};

  return (
    <main className="app-main" data-testid="admin-page" style={{ height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div
        className="mx-auto flex w-full max-w-[1500px] flex-col gap-6 px-4 py-5 sm:px-6 lg:px-8"
        style={{ flex: 1, overflowY: activeTab === 'overview' && !overviewExpanded ? 'hidden' : 'auto' }}
      >
        <SectionCard
          title="Advoq admin console"
          body="Manage platform access, community-tier rollouts, and firms without exposing end-user activity. This view intentionally focuses on identity, access, and onboarding state only."
          action={(
            <div className="rounded-2xl px-4 py-3 text-right" style={{ background: 'var(--bg-elevated)' }}>
              <p className="text-xs uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>Your access</p>
              <p className="mt-1 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{user?.platform_role}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
            </div>
          )}
        >
          <div className="grid gap-4 lg:grid-cols-5">
            <StatCard icon={Users} label="Users" value={overview.total_users || 0} note="Visible as names, emails, access, plan, and onboarding state." />
            <StatCard icon={Shield} label="Advoq Admins" value={overview.total_admins || 0} note="Owners are tracked separately." />
            <StatCard icon={Crown} label="Owners" value={overview.total_owners || 0} note="Only owners can demote Advoq admins or approve pending community tiers." />
            <StatCard icon={KeyRound} label="Community Tiers" value={overview.active_community_programs || 0} note={`${overview.pending_community_programs || 0} pending owner review.`} />
            <StatCard icon={Building2} label="Teams" value={overview.active_teams || 0} note={`${overview.team_memberships || 0} seats mapped across onboarded firms.`} />
          </div>

          <div className="mt-5 rounded-2xl p-4" style={{ background: 'var(--bg-elevated)' }}>
            <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              Staff test mode
            </p>
            <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              Owners and Advoq admins keep admin-console access even while simulating a user subscription inside the product.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="advoq-btn-secondary"
                disabled={busyKey === 'switch-live' || !user?.dashboard_switch_active}
                onClick={() => switchContext({ clear: true })}
              >
                Live Staff Access
              </button>
              {TEST_PLAN_OPTIONS.map((plan) => (
                <button
                  key={plan}
                  type="button"
                  className="advoq-btn-secondary"
                  disabled={busyKey === `switch-${plan}` || user?.plan === plan}
                  onClick={() => switchContext({ plan })}
                >
                  Test as {plan}
                </button>
              ))}
            </div>
          </div>
        </SectionCard>

        <div className="flex flex-wrap gap-2">
          {[
            ['overview', 'Overview'],
            ['users', 'Users'],
            ['community', 'Community'],
            ['teams', 'Teams'],
          ].map(([tabId, label]) => (
            <button
              key={tabId}
              type="button"
              className="rounded-full px-4 py-2 text-sm font-medium"
              style={{
                background: activeTab === tabId ? 'var(--accent-glow)' : 'var(--bg-elevated)',
                color: activeTab === tabId ? 'var(--accent-primary)' : 'var(--text-secondary)',
                border: `1px solid ${activeTab === tabId ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
              }}
              onClick={() => setActiveTab(tabId)}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="grid gap-4 xl:grid-cols-2">
            <SectionCard title="Access breakdown" body="Platform access is now distinct from the user’s professional role.">
              <div className="space-y-3">
                {Object.entries(metrics?.users_by_access || {}).slice(0, overviewExpanded ? undefined : 3).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between rounded-xl px-4 py-3" style={{ background: 'var(--bg-elevated)' }}>
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{key}</span>
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{value}</span>
                  </div>
                ))}
              </div>
            </SectionCard>

            <SectionCard title="Plan breakdown" body="Community-tier access is separate from paid plans and is driven by approved programs plus access codes.">
              <div className="space-y-3">
                {Object.entries(metrics?.users_by_plan || {}).slice(0, overviewExpanded ? undefined : 3).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between rounded-xl px-4 py-3" style={{ background: 'var(--bg-elevated)' }}>
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{key}</span>
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{value}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
            {!overviewExpanded && (
              <div className="xl:col-span-2 flex justify-end">
                <button type="button" className="advoq-btn-secondary text-sm" onClick={() => setOverviewExpanded(true)}>
                  Expand admin overview
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <SectionCard title={`Users (${users.length})`} body="Advoq staff can see names and email IDs across the platform, but not user activity within free, community-tier, or paid accounts.">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px]">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    {['Name', 'Email', 'Professional role', 'Access', 'Plan', 'Community info', 'Teams', 'Joined', 'Actions'].map((label) => (
                      <th key={label} className="px-3 py-3 text-left text-xs uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((member) => {
                    const canEditPlatformRole = member.platform_role !== 'OWNER' && (isOwner || (member.platform_role === 'USER' && member.plan === 'FREE'));
                    const canDelete = isOwner && member.platform_role !== 'OWNER';
                    const firstActiveProgram = activePrograms[0]?.program_id || '';
                    return (
                      <tr key={member.user_id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td className="px-3 py-3 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{member.name || 'Unnamed user'}</td>
                        <td className="px-3 py-3 text-sm" style={{ color: 'var(--text-secondary)', fontFamily: "'JetBrains Mono', monospace" }}>{member.email}</td>
                        <td className="px-3 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>{member.role || 'STUDENT'}</td>
                        <td className="px-3 py-3">
                          {member.platform_role === 'OWNER' ? (
                            <span className="rounded-full px-3 py-1 text-xs font-semibold" style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}>OWNER</span>
                          ) : (
                            <select
                              className="advoq-input text-sm"
                              value={member.platform_role}
                              disabled={!canEditPlatformRole || busyKey === `user-${member.user_id}`}
                              onChange={(event) => updateUser(member, { platform_role: event.target.value }, 'Access updated')}
                            >
                              {['USER', 'ADMIN'].map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <select
                            className="advoq-input text-sm"
                            value={member.plan}
                            disabled={member.platform_role === 'OWNER' || busyKey === `user-${member.user_id}`}
                            onChange={(event) => updateUser(
                              member,
                              {
                                plan: event.target.value,
                                ...(event.target.value === 'COMMUNITY' ? { community_program_id: member.community_program?.program_id || firstActiveProgram } : {}),
                              },
                              'Plan updated',
                            )}
                          >
                            {PLAN_OPTIONS.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-3 text-sm">
                          {member.plan === 'COMMUNITY' ? (
                            <div className="flex items-center gap-2">
                              <select
                                className="advoq-input text-sm"
                                value={member.community_program?.program_id || activePrograms[0]?.program_id || ''}
                                disabled={busyKey === `user-${member.user_id}` || activePrograms.length === 0}
                                onChange={(event) => updateUser(
                                  member,
                                  { plan: 'COMMUNITY', community_program_id: event.target.value },
                                  'Community-tier program updated',
                                )}
                              >
                                {activePrograms.map((program) => (
                                  <option key={program.program_id} value={program.program_id}>{program.name}</option>
                                ))}
                              </select>
                              {member.community_program ? (
                                <button
                                  type="button"
                                  className="app-sidebar__icon-button"
                                  title={`${member.community_program.display_name}: ${member.community_program.target_market}. Features: ${(member.community_program.enabled_features || []).join(', ') || 'Custom'}.`}
                                >
                                  <Info size={15} />
                                </button>
                              ) : null}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>No community tier</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {(member.team_memberships || []).length
                            ? member.team_memberships.map((team) => `${team.name} (${team.team_role})`).join(', ')
                            : 'No team'}
                        </td>
                        <td className="px-3 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'Unknown'}
                        </td>
                        <td className="px-3 py-3">
                          {canDelete ? (
                            <button
                              type="button"
                              className="app-sidebar__icon-button"
                              disabled={busyKey === `delete-${member.user_id}`}
                              onClick={() => handleDeleteUser(member)}
                              title="Delete user"
                            >
                              <Trash2 size={15} />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SectionCard>
        )}

        {activeTab === 'community' && (
          <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <SectionCard title="Community-tier programs" body="Access codes only unlock community-tier programs. They do not grant paid plans or full admin access.">
              <div className="space-y-4">
                {programs.map((program) => (
                  <div key={program.program_id} className="rounded-2xl border p-4" style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-surface)' }}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{program.name}</p>
                        <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>{program.target_market}</p>
                      </div>
                      <span className="rounded-full px-3 py-1 text-xs font-semibold" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                        {program.status}
                      </span>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <div>
                        <p className="text-xs uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>Enabled features</p>
                        <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>{(program.enabled_features || []).join(', ') || 'Custom community access'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>Access code</p>
                        <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)', fontFamily: "'JetBrains Mono', monospace" }}>{program.access_code}</p>
                      </div>
                    </div>
                    <p className="mt-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                      Referrals: {(program.referral_emails || []).length ? program.referral_emails.join(', ') : 'None'}
                    </p>
                    {isOwner && program.status === 'PENDING' ? (
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="advoq-btn-primary"
                          disabled={busyKey === `program-${program.program_id}`}
                          onClick={() => reviewProgram(program.program_id, 'ACTIVE')}
                        >
                          <CheckCircle2 size={16} /> Approve
                        </button>
                        <button
                          type="button"
                          className="advoq-btn-secondary"
                          disabled={busyKey === `program-${program.program_id}`}
                          onClick={() => reviewProgram(program.program_id, 'DENIED')}
                        >
                          <XCircle size={16} /> Deny
                        </button>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </SectionCard>

            <SectionCard title="Create community-tier rollout" body={isOwner ? 'Owner-created programs go live immediately.' : 'Admin-created programs go to the owners for approval, then notify referrals on approval.'}>
              <form className="space-y-4" onSubmit={handleCreateProgram}>
                <input
                  className="advoq-input w-full"
                  value={programForm.name}
                  onChange={(event) => setProgramForm((current) => ({ ...current, name: event.target.value }))}
                  placeholder="Program name"
                  required
                />
                <textarea
                  className="advoq-input min-h-[110px] w-full"
                  value={programForm.target_market}
                  onChange={(event) => setProgramForm((current) => ({ ...current, target_market: event.target.value }))}
                  placeholder="Who this community-tier access is for"
                  required
                />
                <input
                  className="advoq-input w-full"
                  value={programForm.enabled_features}
                  onChange={(event) => setProgramForm((current) => ({ ...current, enabled_features: event.target.value }))}
                  placeholder="Enabled features or agents, comma-separated"
                />
                <textarea
                  className="advoq-input min-h-[110px] w-full"
                  value={programForm.usage_limits}
                  onChange={(event) => setProgramForm((current) => ({ ...current, usage_limits: event.target.value }))}
                  placeholder='Usage limits as JSON or one per line, e.g. chats_per_month: 100'
                />
                <textarea
                  className="advoq-input min-h-[90px] w-full"
                  value={programForm.referral_emails}
                  onChange={(event) => setProgramForm((current) => ({ ...current, referral_emails: event.target.value }))}
                  placeholder="Initial referral emails, comma-separated"
                />
                <button type="submit" className="advoq-btn-primary" disabled={busyKey === 'create-program'}>
                  {busyKey === 'create-program' ? 'Saving…' : 'Submit program'}
                </button>
              </form>
            </SectionCard>
          </div>
        )}

        {activeTab === 'teams' && (
          <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <SectionCard title="Onboarded firms and teams" body="Team admins inside a firm can promote members into team-admin status, but only Advoq owners/admins can demote a team admin.">
              <div className="space-y-4">
                {teams.length ? teams.map((team) => (
                  <TeamCard
                    key={team.team_id}
                    team={team}
                    saving={busyKey === `team-${team.team_id}`}
                    onSave={updateTeamMember}
                  />
                )) : (
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No teams onboarded yet.</p>
                )}
              </div>
            </SectionCard>

            <SectionCard title="Create team" body="Use this to onboard a firm, seed its first team admins, and map the rest of the members into the team plan.">
              <form className="space-y-4" onSubmit={handleCreateTeam}>
                <input
                  className="advoq-input w-full"
                  value={teamForm.name}
                  onChange={(event) => setTeamForm((current) => ({ ...current, name: event.target.value }))}
                  placeholder="Team or firm name"
                  required
                />
                <textarea
                  className="advoq-input min-h-[110px] w-full"
                  value={teamForm.admin_emails}
                  onChange={(event) => setTeamForm((current) => ({ ...current, admin_emails: event.target.value }))}
                  placeholder="Initial team admin emails, comma-separated"
                />
                <textarea
                  className="advoq-input min-h-[110px] w-full"
                  value={teamForm.member_emails}
                  onChange={(event) => setTeamForm((current) => ({ ...current, member_emails: event.target.value }))}
                  placeholder="Member emails, comma-separated"
                />
                <button type="submit" className="advoq-btn-primary" disabled={busyKey === 'create-team'}>
                  {busyKey === 'create-team' ? 'Saving…' : 'Create team'}
                </button>
              </form>
            </SectionCard>
          </div>
        )}
      </div>
    </main>
  );
}
